import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { GriddSnapshot } from "@gridd/embed/react";

import { Slide } from "@/components/deck/Slide";
import { slideNumbering } from "@/lib/slide-numbering";
import { SLIDE_W } from "@/lib/slide-size";
import type { SlideData } from "@/types/document";

// Export to PDF via the browser's print-to-PDF (TODO §6) — "presentation view, but
// paginated for print". It portals a print layer to <body> (a sibling of #root, so the
// print stylesheet in `styles.css` can hide everything else), stacks every ACTIVE slide as
// a page-sized box, then calls `window.print()`. The user picks "Save as PDF" — no PDF
// library, matching the plan.
//
// The desktop shell (§14) takes the same layer down a different last step: pass `capture`
// and the dialog is skipped, because there is no print preview in Electron to read the
// `@page` rule and the OS dialog would print a 16:9 slide onto upright system paper. Main
// renders this very layer instead, with the page box set from the slide size — everything
// above this line is identical in both runtimes, which is the point.
//
// Reuses the same `Slide` in `presenting` mode as the editor and presentation view, so the
// printed page is pixel-identical to what you edited (§6.1). Each slide's fixed 1600×900
// logical canvas maps 1:1 onto the 1600×900 `@page`, so `Slide` renders at scale 1 and 1
// slide = 1 page. Page numbers/footers live on the canvas (§7), so they print for free
// (§6.4).
//
// §6.6 — Gridd embeds in print: instead of mounting cross-origin iframes (which print
// unreliably), the export flow pre-captures a static snapshot of each live editor grid and
// hands them in via `snapshots`. Each grid window then renders that image, so the print
// layer holds ZERO iframes. Any grid without a snapshot (capture failed/timed out) falls
// back to a live embed — the §6.3 blank-risk, now the rare exception rather than the rule.

const noop = () => {};

// A short buffer for locally-decoded assets — the snapshot PNGs and image blocks — to
// paint before printing. No longer gated on cross-origin iframes loading (grids arrive as
// ready snapshots now, §6.6), so this is far shorter than the old iframe-settle delay.
const PRINT_SETTLE_MS = 500;

export function PrintView({
  slides,
  title,
  snapshots,
  onFinished,
  capture,
}: {
  // ACTIVE (non-archived) slides only — the caller filters, so indices are the page order
  // and feed the on-slide page number directly (§6.4).
  slides: SlideData[];
  // The deck's title, which becomes the exported PDF's own title (see below).
  title: string;
  // Pre-captured grid snapshots keyed by griddDocId (§6.6.4), threaded to each window.
  snapshots: Record<string, GriddSnapshot>;
  // Called once printing is done (dialog dismissed) so the caller can drop print mode.
  onFinished: () => void;
  // Desktop only (§14.7.4): render this layer to a PDF file instead of opening the print
  // dialog. Called once the layer has settled; resolves when the file is written or the
  // user cancelled. Absent on the web, where the dialog IS the export.
  capture?: () => Promise<void>;
}) {
  // Both callbacks are read once, when the timer fires. Latching them keeps the effect a
  // mount-once effect: an inline arrow from a re-rendering parent must not restart the
  // settle timer half way through, or reopen the dialog behind it.
  const latest = useRef({ onFinished, capture });
  latest.current = { onFinished, capture };

  // A PDF's own `/Title` — what a viewer shows in its title bar, and what the browser's
  // print dialog offers as the file name — is taken from the *document* title, and neither
  // runtime gives us another way to set it. Untouched, that is the static app name from
  // `index.html`, so every exported deck came out called "DeckAI — Slide Editor" unless the
  // user retyped it in the save panel. Borrow the document title for the length of the
  // export and hand it back afterwards; a blank deck title keeps the app name rather than
  // stamping an empty one.
  useEffect(() => {
    const wanted = title.trim();
    if (!wanted) return;
    const previous = document.title;
    document.title = wanted;
    return () => {
      document.title = previous;
    };
  }, [title]);

  useEffect(() => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      latest.current.onFinished();
    };
    // Desktop: main prints the page itself, so there is no dialog and no `afterprint` — the
    // export is over when the PDF has been written (or the save panel was dismissed). A
    // failure still has to release print mode, or the layer would cover the editor forever.
    if (latest.current.capture) {
      const timer = setTimeout(() => {
        void latest.current
          .capture?.()
          .catch((err) => {
            console.warn("[print] desktop PDF export failed", err);
          })
          .finally(finish);
      }, PRINT_SETTLE_MS);
      return () => clearTimeout(timer);
    }
    // `afterprint` is the reliable "dialog closed" signal in modern browsers.
    window.addEventListener("afterprint", finish);
    const timer = setTimeout(() => {
      window.print();
      // Fallback for engines that don't fire `afterprint`: `window.print()` blocks until
      // the dialog returns, so finishing on the next tick is safe (and a no-op if
      // `afterprint` already fired).
      setTimeout(finish, 0);
    }, PRINT_SETTLE_MS);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("afterprint", finish);
    };
  }, []);

  // §19.0.3/§18.2.9 One numbering pass for the whole printed deck, so a cover consumes no
  // page number and the PDF agrees with the editor — down to the `4.2` on an evidence slide,
  // which is the number someone reads off a printout to say where they are.
  const numbers = slideNumbering(slides);

  return createPortal(
    <div className="slidecraft-print fixed inset-0 z-[100] overflow-auto bg-white">
      <div className="slidecraft-print-hint sticky top-0 z-10 bg-slate-900/90 px-4 py-2 text-center text-sm text-white">
        {capture
          ? "Preparing PDF… choose where to save it."
          : "Preparing PDF… choose “Save as PDF” in the print dialog."}
      </div>
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="slidecraft-print-page mx-auto"
          style={{ width: SLIDE_W }}
        >
          <Slide
            slide={s}
            index={i}
            pageNumber={numbers[i]}
            onChange={noop}
            presenting
            snapshots={snapshots}
          />
        </div>
      ))}
    </div>,
    document.body,
  );
}
